import { z } from "zod";

// File URLs must be https (blocks javascript:/data:/http SSRF payloads being stored).
const httpsUrl = z
  .string()
  .url()
  .refine((u) => u.startsWith("https://"), "Debe ser una URL https://");

export const feeStatusEnum = z.enum(["PENDING", "PAID", "OVERDUE", "PARTIAL"]);
export const expenseStatusEnum = z.enum(["PENDING", "APPROVED", "PAID"]);
export const budgetStatusEnum = z.enum(["DRAFT", "APPROVED"]);
export const reportTypeEnum = z.enum(["MONTHLY", "ANNUAL", "BUDGET_VS_ACTUAL"]);

export const createFeeSchema = z.object({
  unitId: z.string().min(1),
  amount: z.coerce.number().positive("El monto debe ser positivo"),
  dueDate: z.coerce.date(),
  period: z.string().min(1, "Período requerido (ej. 2026-05)"),
});
export type CreateFeeInput = z.infer<typeof createFeeSchema>;

// Each selected unit is billed its own Unit.monthlyFee for the period; units
// without a fee set are skipped and reported back. For a one-off amount (e.g. a
// fine), use createFeeSchema on a single unit instead.
export const bulkGenerateFeesSchema = z.object({
  dueDate: z.coerce.date(),
  period: z.string().min(1),
  unitIds: z.array(z.string()).optional(),
});

export const payFeeSchema = z.object({
  paymentMethod: z.string().min(1, "Método de pago requerido"),
  transactionRef: z.string().optional().nullable(),
  paidAt: z.coerce.date().optional(),
  // Omit to pay the full remaining balance (unchanged default behavior).
  // A lesser amount records a partial payment.
  amount: z.coerce.number().positive().optional(),
});

// A reminder may carry the administration's own note (a payment plan, a contact).
export const remindFeeSchema = z.object({
  note: z.string().trim().max(1000).optional().nullable(),
});

// Undoing the last recorded payment of a fee (a payment entered by mistake).
export const unpayFeeSchema = z.object({
  reason: z.string().trim().max(500).optional().nullable(),
});

// The payment reminder's template: plain text with fields — {{fecha}}, {{apto}}, {{balance}}
// and {{nota}} — Spanish, then English (which may be left empty).
export const feeReminderTemplateSchema = z.object({
  subject: z.string().trim().min(1, "El asunto es requerido").max(300),
  bodyEs: z.string().trim().min(1, "El texto en español es requerido").max(5000),
  bodyEn: z.string().trim().max(5000).default(""),
});

// The automatic fee reminders: on or off, the days of the month (1–28, so every month has
// them) and the hour they go out, and the template — the same one "Enviar Recordatorio" uses.
export const feeReminderSettingsSchema = feeReminderTemplateSchema.extend({
  enabled: z.boolean(),
  days: z
    .array(z.number().int().min(1, "Días del 1 al 28").max(28, "Días del 1 al 28"))
    .min(1, "Elija al menos un día")
    .transform((d) => [...new Set(d)].sort((a, b) => a - b)),
  hour: z.number().int().min(6, "Hora entre 6:00 a. m. y 8:00 p. m.").max(20, "Hora entre 6:00 a. m. y 8:00 p. m."),
});
export type FeeReminderSettings = z.infer<typeof feeReminderSettingsSchema>;
export type FeeReminderTemplate = z.infer<typeof feeReminderTemplateSchema>;

export const createExpenseSchema = z.object({
  category: z.string().min(1),
  description: z.string().min(1),
  amount: z.coerce.number().positive(),
  vendor: z.string().optional().nullable(),
  receiptUrl: httpsUrl.optional().nullable(),
  // Optional: real condos have unbudgeted/emergency spend. When set, this
  // expense counts toward that budget category's live "spent" figure once
  // it reaches APPROVED or PAID.
  budgetCategoryId: z.string().min(1).optional().nullable(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

// Admin/finance corrections — every field optional.
export const updateFeeSchema = createFeeSchema
  .extend({ status: feeStatusEnum })
  .partial();
export type UpdateFeeInput = z.infer<typeof updateFeeSchema>;

// Status is deliberately NOT editable here: approving/paying goes through
// POST /expenses/:id/review, which enforces segregation of duties. Letting the
// generic update carry `status` let a submitter approve their own expense.
export const updateExpenseSchema = createExpenseSchema.partial();
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

export const reviewExpenseSchema = z.object({
  decision: z.enum(["APPROVED", "PAID", "REJECTED"]),
  notes: z.string().optional().nullable(),
});

export const createBudgetSchema = z.object({
  fiscalYear: z.string().regex(/^\d{4}-\d{4}$/, "Formato: 2026-2027"),
  totalAmount: z.coerce.number().positive(),
  fileUrl: httpsUrl.optional().nullable(),
  categories: z
    .array(
      z.object({
        categoryName: z.string().min(1),
        allocatedAmount: z.coerce.number().nonnegative(),
      })
    )
    .optional()
    .default([]),
});

export const updateBudgetSchema = createBudgetSchema
  .extend({ status: budgetStatusEnum })
  .partial();
export type UpdateBudgetInput = z.infer<typeof updateBudgetSchema>;

export const createReportSchema = z.object({
  reportType: reportTypeEnum,
  periodYear: z.coerce.number().int(),
  periodMonth: z.coerce.number().int().min(1).max(12).optional().nullable(),
  fileUrl: httpsUrl,
});

export const summaryBreakdownItemSchema = z.object({
  label: z.string().min(1).max(80),
  amount: z.coerce.number(),
  kind: z.enum(["INCOME", "EXPENSE"]),
});
export type SummaryBreakdownItem = z.infer<typeof summaryBreakdownItemSchema>;

// Monthly close: the bookkeeper publishes headline figures + the P&L PDF.
// netIncome is computed server-side (totalIncome - totalExpenses).
export const upsertMonthlySummarySchema = z.object({
  periodYear: z.coerce.number().int().min(2000).max(2100),
  periodMonth: z.coerce.number().int().min(1).max(12),
  totalIncome: z.coerce.number().nonnegative(),
  totalExpenses: z.coerce.number().nonnegative(),
  reserveFund: z.coerce.number().nonnegative().optional().nullable(),
  breakdown: z.array(summaryBreakdownItemSchema).max(20).optional(),
  notes: z.string().max(2000).optional().nullable(),
  reportFileUrl: httpsUrl.optional().nullable(),
  publish: z.boolean().default(false),
});
export type UpsertMonthlySummaryInput = z.infer<typeof upsertMonthlySummarySchema>;
